import { useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "@/i18n/I18nProvider";
import { devWarn } from "@/lib/devlog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Success screen shown after successful waitlist submission.
 * Displays the user's unique referral link, share buttons, and live count of people they've invited.
 * `entry` is the WaitlistEntry returned by the API (contains referral_code + nome).
 */
export default function ReferralSuccess({ entry }) {
    const { t } = useI18n();
    const firstName = (entry?.nome || "").split(" ")[0] || "";
    const code = entry?.referral_code || "";
    const shareUrl = code
        ? `${(typeof window !== "undefined" && window.location.origin) || "https://appitly.com"}/?ref=${code}`
        : "";
    const shareText = t("ref.shareText");

    const [copied, setCopied] = useState(false);
    const [stats, setStats] = useState({ invited: 0, total: null });

    useEffect(() => {
        if (!code) return;
        let alive = true;
        const load = async () => {
            try {
                const { data } = await axios.get(`${BACKEND_URL}/api/waitlist/referral/${code}`);
                if (!alive) return;
                setStats({
                    invited: data.invited || 0,
                    total: data.total_waitlist || null,
                });
            } catch (err) {
                if (alive) devWarn("[ITLY] referral stats fetch failed:", err?.message || err);
            }
        };
        load();
        const timer = setInterval(load, 30000);
        return () => { alive = false; clearInterval(timer); };
    }, [code]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch {
            const input = document.getElementById("itly-referral-link");
            if (input) {
                input.select();
                document.execCommand?.("copy");
                setCopied(true);
                setTimeout(() => setCopied(false), 2200);
            }
        }
    };

    const encoded = encodeURIComponent(`${shareText} ${shareUrl}`);
    const shareLinks = {
        whatsapp: `https://api.whatsapp.com/send?text=${encoded}`,
        telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
        email: `mailto:?subject=${encodeURIComponent(t("ref.emailSubject"))}&body=${encoded}`,
        twitter: `https://twitter.com/intent/tweet?text=${encoded}`,
    };

    return (
        <section
            id="waitlist"
            data-testid="waitlist-section"
            style={{
                background: "var(--itly-teal-900)",
                color: "var(--itly-on-teal)",
                padding: "96px 32px",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    top: -160,
                    right: -160,
                    width: 460,
                    height: 460,
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(232,113,76,0.18) 0%, rgba(232,113,76,0) 70%)",
                }}
            />

            <div
                style={{
                    position: "relative",
                    maxWidth: 620,
                    margin: "0 auto",
                    textAlign: "center",
                }}
                data-testid="waitlist-success"
            >
                <div
                    style={{
                        width: 68,
                        height: 68,
                        borderRadius: 999,
                        background: "rgba(232, 113, 76, 0.18)",
                        color: "var(--itly-orange-500)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 24,
                    }}
                    aria-hidden="true"
                >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                </div>

                <h2
                    className="itly-serif"
                    style={{
                        margin: 0,
                        fontSize: "clamp(34px, 4.4vw, 54px)",
                        lineHeight: 1.05,
                        letterSpacing: "-0.022em",
                    }}
                >
                    {firstName ? t("ref.titleWithName", { name: firstName }) : t("ref.title")}
                </h2>

                <p
                    style={{
                        marginTop: 18,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 17,
                        lineHeight: 1.6,
                        color: "rgba(247, 244, 238, 0.78)",
                        maxWidth: 500,
                        marginLeft: "auto",
                        marginRight: "auto",
                    }}
                >
                    {t("ref.sub")}
                </p>

                <div
                    style={{
                        marginTop: 32,
                        background: "rgba(247, 244, 238, 0.07)",
                        border: "1px solid rgba(247, 244, 238, 0.18)",
                        borderRadius: 20,
                        padding: "24px 22px",
                        textAlign: "left",
                    }}
                >
                    <div
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: "var(--itly-orange-500)",
                            marginBottom: 10,
                        }}
                    >
                        {t("ref.personalLink")}
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "stretch",
                            gap: 8,
                            flexWrap: "wrap",
                        }}
                    >
                        <input
                            id="itly-referral-link"
                            data-testid="referral-link"
                            readOnly
                            value={shareUrl}
                            onFocus={(e) => e.currentTarget.select()}
                            style={{
                                flex: "1 1 260px",
                                minWidth: 0,
                                background: "rgba(247, 244, 238, 0.06)",
                                border: "1px solid rgba(247, 244, 238, 0.2)",
                                borderRadius: 10,
                                color: "var(--itly-on-teal)",
                                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                                fontSize: 13,
                                padding: "12px 14px",
                                outline: "none",
                            }}
                        />
                        <button
                            type="button"
                            onClick={copy}
                            data-testid="referral-copy"
                            className="itly-btn-primary"
                            style={{
                                background: copied ? "#25A26A" : "var(--itly-orange-500)",
                                color: "#fff",
                                padding: "0 16px",
                                minHeight: 44,
                            }}
                        >
                            {copied ? (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                                    {t("ref.copied")}
                                </>
                            ) : (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
                                    {t("ref.copy")}
                                </>
                            )}
                        </button>
                    </div>

                    <div
                        style={{
                            marginTop: 16,
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                        }}
                        data-testid="referral-share"
                    >
                        <ShareButton href={shareLinks.whatsapp} label="WhatsApp" testid="share-whatsapp">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 3.5A11.5 11.5 0 0 0 3 20.4L2 22l1.7-.5A11.5 11.5 0 1 0 20.5 3.5Zm-8.5 18a9.5 9.5 0 0 1-4.9-1.4l-.35-.2-3.4 1 1-3.3-.2-.35A9.5 9.5 0 1 1 12 21.5Zm5.3-7.2c-.3-.15-1.7-.85-2-.94-.27-.1-.47-.15-.66.14-.2.3-.75.94-.92 1.14-.17.2-.34.22-.63.07-.3-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.75-1.63-2.05-.17-.3-.02-.46.13-.6.14-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.58-.9-2.17-.24-.58-.48-.5-.66-.5l-.57-.02c-.2 0-.5.07-.77.37-.27.3-1.02 1-1.02 2.43 0 1.44 1.04 2.83 1.19 3.02.14.2 2.06 3.14 5 4.4.7.3 1.25.48 1.68.62.7.22 1.35.2 1.86.12.57-.08 1.75-.72 2-1.4.25-.7.25-1.28.17-1.4-.08-.13-.27-.2-.57-.35Z"/></svg>
                        </ShareButton>
                        <ShareButton href={shareLinks.telegram} label="Telegram" testid="share-telegram">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="m9.4 15.6-.3 4c.4 0 .6-.2.9-.4l2-2 4.3 3.2c.8.4 1.3.2 1.5-.7L21 5.4c.3-1.2-.4-1.7-1.2-1.4L3.7 10c-1.1.4-1.1 1-.2 1.3l4 1.2 9.4-5.9c.4-.3.8-.1.5.2Z"/></svg>
                        </ShareButton>
                        <ShareButton href={shareLinks.twitter} label="X / Twitter" testid="share-twitter">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z"/></svg>
                        </ShareButton>
                        <ShareButton href={shareLinks.email} label="Email" testid="share-email">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
                        </ShareButton>
                    </div>

                    <div
                        style={{
                            marginTop: 20,
                            paddingTop: 18,
                            borderTop: "1px solid rgba(247, 244, 238, 0.14)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 8,
                            fontFamily: "Inter, sans-serif",
                            fontSize: 13.5,
                            color: "rgba(247, 244, 238, 0.72)",
                        }}
                        data-testid="referral-stats"
                    >
                        <span>
                            {t("ref.invited_a")}{" "}
                            <strong style={{ color: "var(--itly-on-teal)", fontWeight: 700 }}>
                                {stats.invited}
                            </strong>{" "}
                            {stats.invited === 1 ? t("ref.invitedFriendOne") : t("ref.invitedFriendMany")}
                        </span>
                        {stats.total !== null && (
                            <span>
                                {t("ref.totalList")}{" "}
                                <strong style={{ color: "var(--itly-on-teal)", fontWeight: 700 }}>
                                    {stats.total}
                                </strong>
                            </span>
                        )}
                    </div>
                </div>

                <p
                    style={{
                        marginTop: 22,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 12.5,
                        color: "rgba(247, 244, 238, 0.55)",
                        letterSpacing: "0.005em",
                    }}
                >
                    {t("ref.yourCode")} <span style={{ color: "var(--itly-on-teal)", fontFamily: "'JetBrains Mono', monospace" }}>{code}</span>
                </p>
            </div>
        </section>
    );
}

function ShareButton({ href, label, testid, children }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testid}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "8px 14px",
                borderRadius: 999,
                background: "rgba(247, 244, 238, 0.08)",
                border: "1px solid rgba(247, 244, 238, 0.22)",
                color: "var(--itly-on-teal)",
                textDecoration: "none",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                fontWeight: 500,
                transition: "background 200ms ease, transform 200ms ease",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(247, 244, 238, 0.14)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(247, 244, 238, 0.08)";
            }}
        >
            {children}
            {label}
        </a>
    );
}
